import type { AppProps } from 'next/app';
import { useEffect, useState, useCallback } from 'react';
import { LoggerProvider } from '../contexts/LoggerContext';
import { SettingsProvider } from '../contexts/SettingsContext';
import { AuthProvider, useAuth } from '../contexts/AuthContext';
import { DomainProviders } from '../contexts/DomainProviders';
import { Layout } from '../components/Layout';
import { LoginForm } from '../components/LoginForm';
import { LoadingScreen } from '../components/LoadingScreen';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { PageErrorFallback } from '../components/PageErrorFallback';
import '../styles/globals.css';

function AuthenticatedApp({ Component, pageProps }: Pick<AppProps, 'Component' | 'pageProps'>) {
  const { isAuthenticated, isLoading } = useAuth();
  const [pageKey, setPageKey] = useState(0);

  const handlePageReset = useCallback(() => {
    setPageKey(prev => prev + 1);
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <LoginForm />;
  }

  return (
    <DomainProviders>
      <Layout>
        {/* Inner ErrorBoundary: Catches page-specific errors while keeping navigation functional */}
        <ErrorBoundary key={pageKey} onReset={handlePageReset}>
          <Component {...pageProps} />
        </ErrorBoundary>
      </Layout>
    </DomainProviders>
  );
}

export default function App({ Component, pageProps }: AppProps) {
  const [appKey, setAppKey] = useState(0);

  useEffect(() => {
    // Polyfill for global object issues that may be caused by browser extensions
    if (typeof window !== 'undefined' && !((window as unknown) as { global?: Window }).global) {
      ((window as unknown) as { global: Window }).global = window;
    }
  }, []);

  const handleAppReset = useCallback(() => {
    setAppKey(prev => prev + 1);
  }, []);

  return (
    <AuthProvider>
      <SettingsProvider>
        <LoggerProvider>
          {/* Outer ErrorBoundary: Catches provider/auth errors with full-page fallback */}
          <ErrorBoundary
            key={appKey}
            fallback={<PageErrorFallback onReset={handleAppReset} />}
            onReset={handleAppReset}
          >
            <AuthenticatedApp Component={Component} pageProps={pageProps} />
          </ErrorBoundary>
        </LoggerProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}
